// shiftUtils.ts

import { getShiftGroupConfig } from "./shiftgb-name";

export interface Shift {
  start_time: string;
  end_time: string;
  allday: number;
  auto_schedule: number;
  color: string;
  default_shift: string;
  duration: number | null;
  group_id: string;
  id: number;
  name: string;
  office_id: string;
  sequence: number;
  shift_id: string;
  type: string;
}

export interface TimeRange {
  start: string;
  end: string;
}

/**
 * @param shifts - 시프트 데이터 배열
 * @returns shift_id를 키로 하는 Map
 */
export const createShiftMap = (shifts: Shift[]): Map<string, Shift> => {
  return new Map((shifts ?? []).map((shift) => [shift.shift_id, shift]));
};

/**
 * @param shiftId - 시프트 ID
 * @param shifts - 시프트 데이터 배열
 * @returns 색상 코드 (없으면 기본값 '#000000')
 */
export const getShiftColor = (shiftId: string, shifts: Shift[]): string => {
  const shift = shifts.find((s) => s.shift_id === shiftId);
  return shift?.color || "#000000";
};

/**
 * @param shiftId - 시프트 ID
 * @param shifts - 시프트 데이터 배열
 * @returns 시프트 정보 객체
 */
export const getShiftInfo = (
  shiftId: string,
  shifts: Shift[]
): Shift | null => {
  return shifts.find((s) => s.shift_id === shiftId) || null;
};

export class ShiftMapper {
  private shiftMap: Map<string, Shift>;

  constructor(shifts: Shift[]) {
    this.shiftMap = createShiftMap(shifts);
  }

  getColor(shiftId: string): string {
    return this.shiftMap.get(shiftId)?.color || "#000000";
  }

  getInfo(shiftId: string): Shift | null {
    return this.shiftMap.get(shiftId) || null;
  }

  getName(shiftId: string): string {
    return this.shiftMap.get(shiftId)?.name || "";
  }

  getTimeRange(shiftId: string): TimeRange | null {
    const shift = this.shiftMap.get(shiftId);
    if (!shift) return null;
    return {
      start: shift.start_time,
      end: shift.end_time,
    };
  }
}

export interface GetDefaultShiftsOptions {
  /** true면 default_shift가 "O"(오프)인 시프트 제외 */
  excludeOff?: boolean;
}

/** default_shift가 있고 서브코드가 아닌 시프트만 sequence 순으로 반환 */
export const getDefaultShiftsSortedBySequence = <
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
  },
>(
  shifts: T[],
  options?: GetDefaultShiftsOptions
): T[] => {
  const config = getShiftGroupConfig();
  const { excludeOff = false } = options ?? {};
  return [...shifts]
    .filter((s) => {
      if (!s.default_shift || config.SUB_CODE_LIST.includes(s.default_shift))
        return false;
      if (excludeOff && config.isMainGroup(s.default_shift)) return false;
      return true;
    })
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
};

export interface BuildShiftIdToGroupMainLetterOptions {
  /** false면 D/E/N/O만 대표 문자로 인정 (검증·집계와 동일). true면 M 포함(최소인원 설정 UI 등). */
  includeMid?: boolean;
}

/**
 * shift_gb 그룹 규칙(buildShiftIdToDefaultMap과 동일)으로 각 shift_id → 그룹 대표 근무구분(D/E/N/O/M).
 * default_shift가 null이거나 서브코드(주 등)여도 같은 shift_gb의 메인 행(D/E/N/…)에 귀속.
 */
export function buildShiftIdToGroupMainLetterMap<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    shift_gb?: string | null;
  },
>(
  shifts: T[],
  options?: BuildShiftIdToGroupMainLetterOptions
): Map<string, string> {
  const map = new Map<string, string>();
  if (!shifts?.length) return map;

  const includeMid = options?.includeMid === true;
  const mainLetters = includeMid
    ? (["D", "E", "N", "O", "M"] as const)
    : (["D", "E", "N", "O"] as const);

  const oShift = shifts.find((s) => s.default_shift === "O");
  const oShiftGb = oShift?.shift_gb;

  const shiftGroupsByGb: Record<
    string,
    { shift_id: string; default_shift: string | null }[]
  > = {};

  shifts.forEach((shift) => {
    let shiftGb: string | undefined;
    if (shift.default_shift === "주" && oShiftGb) {
      shiftGb = oShiftGb;
    } else if (shift.default_shift) {
      shiftGb = shift.shift_gb || "etc";
    } else if (shift.shift_gb && oShiftGb && shift.shift_gb === oShiftGb) {
      shiftGb = oShiftGb;
    } else if (shift.shift_gb) {
      shiftGb = shift.shift_gb;
    } else {
      shiftGb = "etc";
    }
    if (!shiftGroupsByGb[shiftGb]) {
      shiftGroupsByGb[shiftGb] = [];
    }
    shiftGroupsByGb[shiftGb].push({
      shift_id: shift.shift_id,
      default_shift: shift.default_shift ?? null,
    });
  });

  Object.entries(shiftGroupsByGb).forEach(([, groupShifts]) => {
    const defaultShift = groupShifts.find(
      (s) =>
        s.default_shift &&
        (mainLetters as readonly string[]).includes(s.default_shift)
    )?.default_shift;
    if (defaultShift) {
      groupShifts.forEach((s) => {
        map.set(s.shift_id, defaultShift);
      });
    }
  });

  return map;
}

/**
 * 최소인원(manPower) 집계·검증용 버킷.
 * - `buildShiftIdToGroupMainLetterMap`으로 같은 shift_gb 서브 shift_id → D/E/N/M 등 대표 문자
 * - 맵에 없으면 `default_shift`가 검증 키에 포함될 때만 사용 (기존 동작 보강)
 */
export function getManpowerBucketForResolvedShift<
  T extends { shift_id: string; default_shift?: string | null },
>(
  resolved: T | undefined,
  shiftIdToGroupMainLetter: Map<string, string>,
  validateKeys: Set<string>
): string | null {
  if (!resolved) return null;
  const fromGroup = shiftIdToGroupMainLetter.get(resolved.shift_id);
  if (fromGroup && validateKeys.has(fromGroup)) return fromGroup;
  const ds = resolved.default_shift;
  if (ds && validateKeys.has(ds)) return ds;
  return null;
}

/**
 * 스케줄 칸: 레거시 문자열 또는 발행 스냅샷 `{ code, color }` → 코드 문자열.
 */
export function normalizeScheduleCellCode(cell: unknown): string {
  if (cell == null) return "";
  if (typeof cell === "string") return cell;
  if (
    typeof cell === "object" &&
    cell !== null &&
    "code" in cell &&
    typeof (cell as { code: unknown }).code === "string"
  ) {
    return (cell as { code: string }).code;
  }
  return "";
}

/**
 * 스케줄 칸 문자열(근무코드)로 시프트 메타 조회.
 * 1) shift_id 일치 2) 없으면 default_shift 일치(스케줄에 D/E/N 등만 올 때 색·이름 매칭)
 * 동일 default_shift가 여러 개면 sequence가 앞선 것(메인에 가깝게).
 */
export function resolveShiftFromScheduleCode<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
  },
>(code: string | null | undefined, shifts: T[]): T | undefined {
  const str = normalizeScheduleCellCode(code);
  if (!str) return undefined;
  const trimmed = str.trim();
  if (!trimmed || trimmed === "-") return undefined;
  const byId = shifts.find((s) => s.shift_id === trimmed);
  if (byId) return byId;
  const byDefault = shifts.filter(
    (s) => s.default_shift != null && s.default_shift === trimmed
  );
  if (byDefault.length > 0) {
    if (byDefault.length === 1) return byDefault[0];
    return [...byDefault].sort(
      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
    )[0];
  }

  // 레거시 코드 fallback (예: Of/Os/OD 등) -> 첫 글자의 메인 default_shift로 매핑
  const head = trimmed.charAt(0).toUpperCase();
  if (["D", "E", "N", "O", "M"].includes(head)) {
    const byLegacyDefault = shifts.filter((s) => s.default_shift === head);
    if (byLegacyDefault.length === 1) return byLegacyDefault[0];
    if (byLegacyDefault.length > 1) {
      return [...byLegacyDefault].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
      )[0];
    }
  }
  return undefined;
}

/** `resolveShiftFromScheduleCode` / Cell 조회용 O(1) 인덱스 (동일 shift_id는 첫 행) */
export type ShiftLookupMaps<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
    id?: number;
  },
> = {
  byNumericId: Map<number, T>;
  byShiftIdFirst: Map<string, T>;
  byDefaultShiftKey: Map<string, T[]>;
};

export function buildShiftLookupMaps<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
    id?: number;
  },
>(shifts: T[]): ShiftLookupMaps<T> {
  const byNumericId = new Map<number, T>();
  const byShiftIdFirst = new Map<string, T>();
  const byDefaultShiftKey = new Map<string, T[]>();

  for (const s of shifts) {
    if (s.id != null && Number.isFinite(Number(s.id))) {
      byNumericId.set(Number(s.id), s);
    }
    if (!byShiftIdFirst.has(s.shift_id)) {
      byShiftIdFirst.set(s.shift_id, s);
    }
    if (s.default_shift != null) {
      const k = s.default_shift;
      let arr = byDefaultShiftKey.get(k);
      if (!arr) {
        arr = [];
        byDefaultShiftKey.set(k, arr);
      }
      arr.push(s);
    }
  }

  return { byNumericId, byShiftIdFirst, byDefaultShiftKey };
}

export function resolveShiftFromScheduleCodeFromMaps<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
  },
>(
  code: string | null | undefined,
  maps: ShiftLookupMaps<T> | undefined
): T | undefined {
  if (maps == null) return undefined;
  const str = normalizeScheduleCellCode(code);
  if (!str) return undefined;
  const trimmed = str.trim();
  if (!trimmed || trimmed === "-") return undefined;
  const byShift = maps.byShiftIdFirst.get(trimmed);
  if (byShift) return byShift;
  const byDefault = maps.byDefaultShiftKey.get(trimmed);
  if (byDefault && byDefault.length > 0) {
    if (byDefault.length === 1) return byDefault[0];
    return [...byDefault].sort(
      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
    )[0];
  }

  // 레거시 코드 fallback (예: Of/Os/OD 등) -> 첫 글자의 메인 default_shift로 매핑
  const head = trimmed.charAt(0).toUpperCase();
  if (["D", "E", "N", "O", "M"].includes(head)) {
    const byLegacyDefault = maps.byDefaultShiftKey.get(head);
    if (byLegacyDefault && byLegacyDefault.length > 0) {
      if (byLegacyDefault.length === 1) return byLegacyDefault[0];
      return [...byLegacyDefault].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
      )[0];
    }
  }
  return undefined;
}

/**
 * 스케줄 API의 `schedule_ids`(숫자 id)가 오면 `/shifts`의 `id`와 매칭해 color·shift_id를 맞춘다.
 * 그 외에는 `schedule` 문자열 기준 `resolveShiftFromScheduleCode`.
 *
 * 시프트 표시는 DB id(`schedule_ids`) 기준 정합을 우선한다.
 * (예: 코드명이 Of→Os로 바뀐 레거시 데이터도 id로 현재 시프트 정의를 정확히 매핑)
 * 미배정(`-`/빈칸)일 때는 id를 쓰지 않는다(옛 id가 남아 색만 남는 현상 방지).
 */
export function resolveShiftFromScheduleCell<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
    id?: number;
  },
>(
  code: string | null | undefined,
  numericId: number | null | undefined,
  shifts: T[]
): T | undefined {
  const str = normalizeScheduleCellCode(code);
  const trimmed = str.trim();
  const isBlankCell = !trimmed || trimmed === "-";

  const byCode = resolveShiftFromScheduleCode(str || null, shifts);

  if (isBlankCell) {
    return byCode;
  }

  if (
    numericId != null &&
    Number.isFinite(Number(numericId)) &&
    shifts?.length
  ) {
    const nid = Number(numericId);
    const byNumericId = shifts.find((s) => s.id === nid);
    if (byNumericId) return byNumericId;
  }
  return byCode;
}

/** `resolveShiftFromScheduleCell`과 동일 규칙, `buildShiftLookupMaps`로 칸당 조회 O(1) 근사 */
export function resolveShiftFromScheduleCellFromMaps<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
    id?: number;
  },
>(
  code: string | null | undefined,
  numericId: number | null | undefined,
  maps: ShiftLookupMaps<T> | undefined
): T | undefined {
  if (maps == null) return undefined;
  const str = normalizeScheduleCellCode(code);
  const trimmed = str.trim();
  const isBlankCell = !trimmed || trimmed === "-";

  const byCode = resolveShiftFromScheduleCodeFromMaps(str || null, maps);

  if (isBlankCell) {
    return byCode;
  }

  if (
    numericId != null &&
    Number.isFinite(Number(numericId)) &&
    maps.byNumericId.size > 0
  ) {
    const nid = Number(numericId);
    const byNumericId = maps.byNumericId.get(nid);
    if (byNumericId) return byNumericId;
  }
  return byCode;
}

/**
 * 집계 맵의 키(스케줄 원문·옛 shift_id)를 현재 shift_id 기준으로 합산.
 * 서버 `counts` 등에 옛 코드가 남아 있을 때 꼬리 통계와 맞춤.
 */
export type AggregateCountKeysMode = "shift_id" | "shift_db_id";

export function aggregateCountKeysByShiftDefinitions<
  T extends {
    shift_id: string;
    default_shift?: string | null;
    sequence?: number;
    id?: number;
  },
>(
  raw: Record<string, number | null | undefined> | null | undefined,
  shifts: T[],
  options?: { keyMode?: AggregateCountKeysMode }
): Record<string, number> {
  if (!raw) return {};
  if (!shifts?.length) {
    return Object.fromEntries(
      Object.entries(raw)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, Number(v)])
    );
  }
  const useDbId = options?.keyMode === "shift_db_id";
  const out: Record<string, number> = {};
  for (const [key, n] of Object.entries(raw)) {
    if (n == null) continue;
    const trimmedKey = key.trim();

    if (useDbId) {
      const idPref = /^id:(\d+)$/.exec(trimmedKey);
      if (idPref) {
        const nid = Number(idPref[1]);
        const idKey = `id:${nid}`;
        out[idKey] = (out[idKey] ?? 0) + Number(n);
        continue;
      }
      if (/^\d+$/.test(trimmedKey)) {
        const nid = Number(trimmedKey);
        if (shifts.some((s) => s.id === nid)) {
          const idKey = `id:${nid}`;
          out[idKey] = (out[idKey] ?? 0) + Number(n);
          continue;
        }
      }
    }

    const resolved = resolveShiftFromScheduleCode(key, shifts);
    if (
      useDbId &&
      resolved?.id != null &&
      Number.isFinite(Number(resolved.id))
    ) {
      const idKey = `id:${resolved.id}`;
      out[idKey] = (out[idKey] ?? 0) + Number(n);
    } else {
      const sid = resolved?.shift_id ?? key;
      out[sid] = (out[sid] ?? 0) + Number(n);
    }
  }
  return out;
}

/**
 * 근무표 스냅샷(defaultShiftCode)과 /shifts API 목록을 id 기준으로 병합.
 * 같은 id가 양쪽에 있으면 `/shifts`(두 번째 인자) 값을 우선 적용한다.
 * 동일 shift_id가 여러 DB 행일 때 schedule_ids로 셀 해석할 수 있게 모든 id 행을 유지한다.
 */
export function mergeShiftsForCellLookup<
  T extends { id?: number; shift_id: string },
>(defaultShiftCode: T[], shiftsData: T[] | undefined | null): T[] {
  const byId = new Map<number, T>();
  const noId: T[] = [];
  const add = (s: T, preferLatest = false) => {
    if (s.id != null && Number.isFinite(Number(s.id))) {
      const id = Number(s.id);
      if (!byId.has(id)) {
        byId.set(id, s);
        return;
      }
      if (preferLatest) {
        const prev = byId.get(id) as T;
        byId.set(id, { ...prev, ...s });
      }
    } else {
      noId.push(s);
    }
  };
  defaultShiftCode.forEach((s) => add(s, false));
  (shiftsData ?? []).forEach((s) => add(s, true));
  return [...byId.values(), ...noId];
}
